
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import Loader from '../../components/ui/Loader';
import Badge from '../../components/ui/Badge';
import { GetJobSeekerDetailsResponse, Attachments, skillLevel, UserDetails } from '../../types/api';
import userService from '../../services/user.service';
import { getProfileCompletionStatus } from '../../utils/profileUtils';
import authService from '../../services/auth.service';
import { LocalStorageUser } from '../../types/localStorage';
import { mapApiToSelectedSkill } from '../../utils/jobSeekerProfileMapper';
import { mapApiCourseToQualification, mapLocation } from '../../utils/jobPostMapper';
import qualificationsData from "../../config/allQualifications.json"; 


interface PublicUserProfileProps {
  userDetails?: UserDetails;
}

const PublicUserProfile: FC<PublicUserProfileProps> = ({ userDetails }) => {
  const { userId } = useParams<{ userId: string }>();
  const location = useLocation();
  const [user, setUser] = useState<UserDetails | null>(userDetails || location.state?.user || null);
  const [jobSeekerData, setJobSeekerData] = useState<GetJobSeekerDetailsResponse | null>(null);
  const [address, setAddress] = useState<String>('N/A');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [completionStatus, setCompletionStatus] = useState<any>(null);

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!userId) {
        setError("User ID is missing.");
        setLoading(false);
        return;
      }
      try {
        if (!user) {
          setLoading(true);
          const response = await authService.getUserDetails(userId);
          setUser(response.data);
        }
        console.log("user [test]",user?.role, userId)
        if (user?.role === 'Jobseeker' || user?.role === 'Intern') {
          const response = await userService.getJobSeekerDetails(userId);
          setJobSeekerData(response.data);
          if(response.data.jobSeekerBasicDetails?.cityId){
            let cityObj = mapLocation(response.data.jobSeekerBasicDetails?.cityId)
            if(cityObj){
              setAddress(cityObj.cityName + ", " + cityObj.stateName)
            } else if (response.data.jobSeekerBasicDetails?.address){
              setAddress(response.data.jobSeekerBasicDetails.address);
            }
          } else if (response.data.jobSeekerBasicDetails?.address){
            setAddress(response.data.jobSeekerBasicDetails.address);
          }
          setCompletionStatus(getProfileCompletionStatus(user as LocalStorageUser, response.data));
        }
      } catch (err) {
        setError("Failed to fetch user details.");
        console.error("Error fetching user details:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserDetails();
  }, [userId, user]);

  const mappedSkills = useMemo(() => {
    if(jobSeekerData?.jobSeekerSkillDetails==null) return []
    const levelToNum = (level: skillLevel): number => {
      switch (level) {
        case "Beginner":
          return 0;
        case "Intermediate":
          return 1;
        case "Expert":
          return 2;
      }
    };
    return mapApiToSelectedSkill(jobSeekerData?.jobSeekerSkillDetails).sort((a,b) => {return levelToNum(b.level) - levelToNum(a.level)})
  },[jobSeekerData]);
  const mappedQualification = useMemo(() => {
    return jobSeekerData?.jobSeekerQualificationDetails
              ? jobSeekerData.jobSeekerQualificationDetails.map((jsq) => {
                  // Find the corresponding qualificationId for the courseId
                  let qualificationId: number = 0;
                  let allowCustomInput = false;
                  let courseName: string | null = null;
                  for (const qData of qualificationsData) {
                    const course = qData.courses.find(
                      (c) => c.courseId === jsq.courseId
                    );
                    if (course) {
                      qualificationId = qData.qualificationId;
                      allowCustomInput = course.allowCustomInput;
                      courseName = course.courseName;
                      break;
                    }
                  }
                  return {
                    qualificationId: qualificationId,
                    courseId: jsq.courseId,
                    courseName: courseName,
                    customCourse: jsq.customCourseName,
                    allowCustomInput: allowCustomInput,
                    isCompleted : jsq.isCompleted
                  };
                })
              : []
      },[jobSeekerData]);

  const getAttachmentLink = (attachment: Attachments) => {
    if (attachment && attachment.fileUrl) {
      return (
        <a href={attachment.fileUrl} target="_blank" rel="noopener noreferrer" className="text-primary-lightGreen hover:underline">
          {attachment.originalName || `Attachment ${attachment.attachmentId}`}
        </a>
      );
    }
    return "N/A";
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-red-600">Error: {error}</div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-12 text-gray-600">No job seeker data found.</div>
    );
  }

  const { jobSeekerBasicDetails, jobSeekerExperienceDetails, jobSeekerQualificationDetails, jobSeekerSkillDetails, jobSeekerPreferredLocationDetails, attachments } = jobSeekerData || {};
  

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {user?.firstName} {user?.lastName}
        </h1>
        <div className="flex">
          {user.role && (
            <div className="mt-4">
              <Badge className="flex-none m-1 ml-0" variant={"primary"}>
                {user.role}
              </Badge>
            </div>
          )}
          {completionStatus && (
            <div className="mt-4">
              <Badge
                className={"flex-none m-1 ml-0 ".concat(
                  completionStatus.percentage >= 80
                    ? "bg-green-100 text-green-800"
                    : "bg-yellow-100 text-yellow-800"
                )}
              >
                Profile Completion: {completionStatus.percentage}%{" "}
                {completionStatus.percentage < 80 &&
                  `(${completionStatus.missingFields.join(", ")} missing)`}
              </Badge>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Basic Info & Contact */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-center">
              <div className="w-24 h-24 bg-gray-200 rounded-lg flex items-center justify-center mx-auto mb-4 overflow-hidden">
                {attachments &&
                attachments.find((att) => att.typeId === "ProfilePhoto")
                  ?.fileUrl ? (
                  <img
                    src={
                      attachments.find((att) => att.typeId === "ProfilePhoto")
                        ?.fileUrl
                    }
                    alt="Profile Photo"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <svg
                    className="w-12 h-12 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                )}
              </div>
              {/* <h2 className="text-xl font-bold text-gray-900">
                {user?.firstName} {user?.lastName}
              </h2> */}
              <p className="text-gray-600 mb-2">{user?.email || "N/A"}</p>
              <p className="text-gray-600 mb-2">
                {user?.mobileNumber || "N/A"}
              </p>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200 space-y-4">
              <div>
                <h3 className="text-md font-semibold text-gray-900 mb-2">
                  Gender
                </h3>
                <p className="text-gray-700">
                  {jobSeekerBasicDetails?.gender || "N/A"}
                </p>
              </div>
              <div>
                <h3 className="text-md font-semibold text-gray-900 mb-2">
                  Address
                </h3>
                <p className="text-gray-700">{address}</p>
                {/* <p className="text-gray-700">{jobSeekerBasicDetails?.cityName || "N/A"}</p> */}
              </div>
            </div>
          </div>
        </div>
        <div className="lg:col-span-2 space-y-6">
          {/* Experience */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Experience
            </h3>
            {jobSeekerExperienceDetails ? (
              <div className="space-y-2">
                <p className="text-gray-700">
                  <span className="text-md font-semibold text-gray-900 mb-2">
                    Fresher:{" "}
                  </span>
                  {jobSeekerExperienceDetails.isFresher ? "Yes" : "No"}
                </p>
                {!jobSeekerExperienceDetails.isFresher && (
                  <>
                    <p className="text-gray-700">
                      <span className="text-md font-semibold text-gray-900 mb-2">
                        {" "}
                        Years of Experience:{" "}
                      </span>
                      {jobSeekerExperienceDetails.yearsOfExperience || "N/A"}
                    </p>

                    <p className="text-gray-700">
                      <span className="text-md font-semibold text-gray-900 mb-2">
                        Current CTC:{" "}
                      </span>
                      {jobSeekerExperienceDetails.currentCTC || "N/A"}
                    </p>
                  </>
                )}
              </div>
            ) : (
              <p className="text-md font-semibold text-gray-900 mb-2">
                No experience details available.
              </p>
            )}
          </div>

          {/* Skills */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex flex-wrap gap-2 m-2">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Skills
              </h3>
              <div>
                <Badge variant="primary" className="mr-2">
                  Expert
                </Badge>
                <Badge variant="secondary" className="mr-2">
                  Intermediate
                </Badge>
                <Badge variant="info" className="mr-2">
                  Beginner
                </Badge>
              </div>
            </div>
            {jobSeekerSkillDetails && jobSeekerSkillDetails.length > 0 ? (
              <>
                <div className="flex flex-wrap gap-2">
                  {mappedSkills.map((skill, index) => (
                    <Badge
                      key={index}
                      variant={
                        skill.level == "Expert"
                          ? "primary"
                          : skill.level == "Intermediate"
                          ? "secondary"
                          : "info"
                      }
                    >
                      {skill.name}
                    </Badge> // Assuming skillId can be mapped to skillName
                  ))}
                </div>
              </>
            ) : (
              <p className="text-gray-700">No skills added.</p>
            )}
          </div>

          {/* Qualifications */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Qualifications
            </h3>
            {jobSeekerQualificationDetails &&
            jobSeekerQualificationDetails.length > 0 ? (
              <div className="space-y-2">
                {mappedQualification.map((qualification, index) => (
                  <div key={index}>
                    <p className="text-gray-700">
                      <span className="text-md font-semibold text-gray-900 mb-2">
                        {"Course Name: "}
                      </span>
                      {qualification.courseName}
                    </p>
                    {/* <p className="text-gray-700">
                      <span className="text-md font-semibold text-gray-900 mb-2">
                        {"Course Id: "}
                      </span>
                      {qualification.courseId}
                    </p> */}
                    {qualification.customCourse && (
                      <p className="text-gray-700">
                        <span className="text-md font-semibold text-gray-900 mb-2">
                          {"Custom Course: "}
                        </span>
                        {qualification.customCourse}
                      </p>
                    )}
                    <p className="text-gray-700">
                      <span className="text-md font-semibold text-gray-900 mb-2">
                        {"Completed: "}
                        {qualification.isCompleted ? "Yes" : "No"}
                      </span>
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-700">No qualifications added.</p>
            )}
          </div>

          {/* Preferred Locations */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Preferred Locations
            </h3>
            {jobSeekerPreferredLocationDetails &&
            jobSeekerPreferredLocationDetails.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {jobSeekerPreferredLocationDetails.map((location, index) => (
                  <Badge key={index} variant="default">
                    {location.cityName}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-gray-700">No preferred locations added.</p>
            )}
          </div>

          {/* Attachments */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Attachments
            </h3>
            {attachments && attachments.length > 0 ? (
              <div className="space-y-2">
                {attachments.map((attachment) => (
                  <div key={attachment.attachmentId}>
                    {getAttachmentLink(attachment)}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-700">No attachments found.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicUserProfile;
